/**
 * Ertzyx Icon System
 * Custom SVG icon components with a unified design language:
 *   - 24x24 viewBox
 *   - stroke-linecap="round" stroke-linejoin="round"
 *   - strokeWidth 1.5 (product) or 1.4 (landing/feature)
 *   - Rounded, archival, human — not generic SaaS admin
 */

import React from 'react';

interface IconProps {
  size?: number;
  strokeWidth?: number;
  className?: string;
  style?: React.CSSProperties;
  color?: string;
}

function Icon({
  size = 20,
  strokeWidth = 1.65,
  className,
  style,
  color = 'currentColor',
  children,
}: IconProps & { children: React.ReactNode }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      vectorEffect="non-scaling-stroke"
      className={className}
      style={style}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

export const ERTZYX_AURORA_GRADIENT =
  'linear-gradient(135deg,#14b8a6 0%,#22d3ee 24%,#7c3aed 52%,#ec4899 74%,#f59e0b 100%)';

export const ERTZYX_SOFT_RAINBOW =
  'linear-gradient(135deg,rgba(20,184,166,0.14),rgba(34,211,238,0.12),rgba(124,58,237,0.12),rgba(236,72,153,0.11),rgba(245,158,11,0.12))';

export function ErtzyxIconBadge({
  children,
  size = 44,
  active = false,
  className,
}: {
  children: React.ReactNode;
  size?: number;
  active?: boolean;
  className?: string;
}) {
  return (
    <span
      className={className}
      style={{
        width: size,
        height: size,
        borderRadius: Math.max(14, size * 0.32),
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        isolation: 'isolate',
        color: active ? '#ffffff' : '#0f766e',
        background: active
          ? ERTZYX_AURORA_GRADIENT
          : `linear-gradient(#ffffff,#ffffff) padding-box, ${ERTZYX_AURORA_GRADIENT} border-box`,
        border: '1px solid transparent',
        boxShadow: active
          ? '0 14px 30px rgba(15,23,42,0.16), 0 0 0 4px rgba(34,211,238,0.12)'
          : '0 10px 24px rgba(15,23,42,0.08)',
        flexShrink: 0,
      }}
    >
      {children}
    </span>
  );
}

// ─── Core Product Icons ───────────────────────────────────────────────────────

/** Open book with layered pages and a bookmark ribbon — Living Ledger */
export function LivingLedgerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="5.5" y="4" width="13" height="15" rx="2" opacity={0.35} />
      <rect x="3.5" y="6" width="13" height="15" rx="2" />
      <path d="M6.5 10h6.5M6.5 13h5M6.5 16h4" />
      <circle cx="16.5" cy="17.5" r="3" />
      <path d="M15.4 17.6l.8.8 1.5-1.7" />
    </Icon>
  );
}

/** Seven vertical columns of varying heights — 7 Pillars */
export function SevenPillarsIcon(props: IconProps) {
  const cols = [
    { x: 1.5, h: 14 },
    { x: 4.5, h: 11 },
    { x: 7.5, h: 15 },
    { x: 10.5, h: 16 },
    { x: 13.5, h: 15 },
    { x: 16.5, h: 11 },
    { x: 19.5, h: 14 },
  ];
  return (
    <Icon {...props}>
      {/* Base line */}
      <line x1="1" y1="21" x2="23" y2="21" />
      {/* Pillars */}
      {cols.map((c, i) => (
        <rect key={i} x={c.x} y={21 - c.h} width={2} height={c.h} rx={0.75} />
      ))}
      {/* Cap line */}
      <line x1="1" y1="4.5" x2="23" y2="4.5" strokeDasharray="1.5 2" />
    </Icon>
  );
}

/** Sealed cylinder jar with wax seal — Time Capsule */
export function TimeCapsuleIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3" y="7" width="18" height="10" rx="5" />
      <path d="M6.5 7v10M17.5 7v10" opacity={0.55} />
      <circle cx="12" cy="12" r="3.1" />
      <path d="M12 10.4v1.9l1.3 1" />
      <path d="M7.5 5.2h9M7.5 18.8h9" />
    </Icon>
  );
}

/** Archival heartbeat line ending in a record dot — Pulse */
export function PulseIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4.5 5.5h15a2 2 0 012 2v7.5a2 2 0 01-2 2H12l-4.5 3v-3h-3a2 2 0 01-2-2V7.5a2 2 0 012-2z" />
      <polyline points="6 12 8.3 12 9.2 9.7 11 14.3 12.5 10.8 13.8 12 18 12" />
    </Icon>
  );
}

/** Curved path with two location pins — Journey Map */
export function JourneyMapIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 17.5c3.2-7.8 8.6 2.8 16-5.8" />
      <circle cx="5" cy="17.5" r="2" />
      <circle cx="12" cy="12.5" r="2" />
      <circle cx="19" cy="8" r="2" />
      <path d="M6.7 7.1c2.2-1.8 5.4-2.6 8.4-1.8" opacity={0.45} />
    </Icon>
  );
}

/** Stacked cards with verification seal — Proof Cards */
export function ProofCardsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Back card (offset) */}
      <rect x="4" y="6" width="14" height="10" rx="1.5" opacity={0.45} />
      {/* Middle card */}
      <rect x="3" y="5" width="14" height="10" rx="1.5" opacity={0.65} />
      {/* Front card */}
      <rect x="2" y="4" width="14" height="10" rx="1.5" />
      {/* Seal on front: circle + check */}
      <circle cx="16" cy="16" r="4.5" />
      <polyline points="14 16 15.3 17.3 18 14.5" />
    </Icon>
  );
}

/** Three-node family tree — Family Archive / Family Ledger */
export function FamilyArchiveIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="9" width="10" height="11" rx="1.6" />
      <path d="M5.5 12h6M5.5 15h4" />
      <path d="M16.5 18V9.5M14.5 18V11M18.5 18V11" />
      <path d="M13.5 9.5l4-3 4 3" />
      <path d="M13.8 18.2h7.4" />
    </Icon>
  );
}

/** Circular vault door with radial handles — Vault */
export function VaultDoorIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M4 9h16l-1.2 10.5a2 2 0 01-2 1.5H7.2a2 2 0 01-2-1.5L4 9z" />
      <path d="M7 9V5.8A1.8 1.8 0 018.8 4h6.4A1.8 1.8 0 0117 5.8V9" />
      <path d="M9 13h6" />
      <circle cx="12" cy="16.5" r="2.2" />
      <path d="M12 15.5v2" />
    </Icon>
  );
}

/** Globe with meridian line and location pin — Geospatial Map */
export function GeospatialIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Globe outline */}
      <circle cx="12" cy="12" r="9.5" />
      {/* Equator (flattened) */}
      <path d="M2.5 12C2.5 12 6 15 12 15s9.5-3 9.5-3" />
      <path d="M2.5 12C2.5 12 6 9 12 9s9.5 3 9.5 3" />
      {/* Prime meridian */}
      <path d="M12 2.5C12 2.5 9 6 9 12s3 9.5 3 9.5" />
      <path d="M12 2.5C12 2.5 15 6 15 12s-3 9.5-3 9.5" />
      {/* Location pin overlay */}
      <path
        d="M15 7.5c0 2-3 5.5-3 5.5S9 9.5 9 7.5a3 3 0 016 0z"
        fill="currentColor"
        strokeWidth={0}
      />
      <circle cx="12" cy="7.5" r="1.2" fill="white" strokeWidth={0} />
    </Icon>
  );
}

/** Premium shield with clean checkmark — Verification */
export function VerificationIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="5" y="3.5" width="14" height="17" rx="2" />
      <path d="M8 7h5.5M8 10.5h4" />
      <circle cx="14.5" cy="15.5" r="3.2" />
      <path d="M13.2 15.6l.9.9 1.8-2" />
    </Icon>
  );
}

/** Three overlapping circles — Circles / Community */
export function CirclesIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="7.5" />
      <circle cx="12" cy="12" r="3" />
      <circle cx="5.5" cy="9" r="1.7" />
      <circle cx="18.5" cy="9" r="1.7" />
      <circle cx="12" cy="19.5" r="1.7" />
      <path d="M7 9.7l2.6 1.2M17 9.7l-2.6 1.2M12 16.5v1.3" />
    </Icon>
  );
}

/** Open book with circle-plus — Add Memory */
export function AddMemoryIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5" width="13" height="15" rx="2" />
      <path d="M7 9h6M7 12h4" />
      <path d="M17.5 3.5l.5 1.4 1.4.5-1.4.5-.5 1.4-.5-1.4-1.4-.5 1.4-.5.5-1.4z" />
      <circle cx="17.5" cy="17.5" r="4" />
      <path d="M17.5 15.4v4.2M15.4 17.5h4.2" />
    </Icon>
  );
}

/** Pinned map with curved route lines — Geospatial/Journey group */
export function JourneyGroupIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Map sheet */}
      <path d="M3 6l6-3 6 3 6-3v15l-6 3-6-3-6 3V6z" />
      {/* Route on map */}
      <path d="M9 3v15" opacity={0.5} />
      <path d="M15 6v15" opacity={0.5} />
      {/* Pin */}
      <path d="M12 11c0 2-2.5 5-2.5 5S7 13 7 11a2.5 2.5 0 015 0z" />
      <circle cx="9.5" cy="11" r="1" fill="currentColor" strokeWidth={0} />
    </Icon>
  );
}

/** Woven/layered archive records — Shared Links */
export function SharedLinksIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4" y="5" width="10" height="12" rx="1.8" />
      <rect x="10" y="7" width="10" height="12" rx="1.8" />
      <path d="M7 9h4M7 12h3M13 11h4M13 14h3" />
      <path d="M12.2 16.8l-1.6 1.6M13.8 7.2l1.6-1.6" />
    </Icon>
  );
}

/** Archive box with upward arrow — Digital Memory Importer */
export function DigitalImporterIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Box body */}
      <path d="M3 9l9-7 9 7v11a2 2 0 01-2 2H5a2 2 0 01-2-2V9z" />
      {/* Box lid flap */}
      <path d="M9 22V11h6v11" />
      {/* Down arrow (into box) */}
      <line x1="12" y1="5" x2="12" y2="9" />
      <polyline points="9.5 7 12 9.5 14.5 7" />
    </Icon>
  );
}

/** Overlapping pages with seal — Media Vault */
export function MediaVaultIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Back document */}
      <rect x="6" y="4" width="12" height="15" rx="1.5" opacity={0.4} />
      {/* Front document */}
      <rect x="3" y="6" width="12" height="15" rx="1.5" />
      {/* Play triangle (media indicator) */}
      <polygon points="8 11 13 13.5 8 16" />
    </Icon>
  );
}

/** Star with pillar rays — Achievements */
export function AchievementsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      {/* Trophy cup */}
      <path d="M8 21h8M12 17v4" />
      <path d="M7 4h10v7a5 5 0 01-10 0V4z" />
      <path d="M7 7H3a2 2 0 000 4h4" />
      <path d="M17 7h4a2 2 0 010 4h-4" />
    </Icon>
  );
}

/** Bell with archive wave — Notifications */
export function NotificationsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M18 8A6 6 0 006 8c0 7-3 9-3 9h18s-3-2-3-9" />
      <path d="M13.73 21a2 2 0 01-3.46 0" />
    </Icon>
  );
}

/** Settings cog — Settings */
export function SettingsIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <circle cx="12" cy="12" r="3" />
      <path d="M19.4 15a1.65 1.65 0 00.33 1.82l.06.06a2 2 0 010 2.83 2 2 0 01-2.83 0l-.06-.06a1.65 1.65 0 00-1.82-.33 1.65 1.65 0 00-1 1.51V21a2 2 0 01-2 2 2 2 0 01-2-2v-.09A1.65 1.65 0 009 19.4a1.65 1.65 0 00-1.82.33l-.06.06a2 2 0 01-2.83 0 2 2 0 010-2.83l.06-.06A1.65 1.65 0 004.68 15a1.65 1.65 0 00-1.51-1H3a2 2 0 01-2-2 2 2 0 012-2h.09A1.65 1.65 0 004.6 9a1.65 1.65 0 00-.33-1.82l-.06-.06a2 2 0 010-2.83 2 2 0 012.83 0l.06.06A1.65 1.65 0 009 4.68a1.65 1.65 0 001-1.51V3a2 2 0 012-2 2 2 0 012 2v.09a1.65 1.65 0 001 1.51 1.65 1.65 0 001.82-.33l.06-.06a2 2 0 012.83 0 2 2 0 010 2.83l-.06.06A1.65 1.65 0 0019.4 9a1.65 1.65 0 001.51 1H21a2 2 0 012 2 2 2 0 01-2 2h-.09a1.65 1.65 0 00-1.51 1z" />
    </Icon>
  );
}

/** Home with archive cube inside — Home */
export function HomeIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <path d="M3 9.5L12 3l9 6.5V20a1 1 0 01-1 1H5a1 1 0 01-1-1V9.5z" />
      <path d="M9 21V13h6v8" />
    </Icon>
  );
}

/** Person silhouette — Profile */
export function ProfileIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="4.5" y="4" width="15" height="16" rx="2" />
      <circle cx="10" cy="10" r="2.3" />
      <path d="M7.3 16c.8-1.8 4.6-1.8 5.4 0" />
      <path d="M14.5 9h2.2M14.5 12h2.2M14.5 15h1.4" />
    </Icon>
  );
}

/** Credit card — Billing */
export function BillingIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="1" y="4" width="22" height="16" rx="2" />
      <line x1="1" y1="10" x2="23" y2="10" />
    </Icon>
  );
}

/** Family tree with three generations — Family Ledger */
export function FamilyLedgerIcon(props: IconProps) {
  return (
    <Icon {...props}>
      <rect x="3.5" y="4" width="17" height="16" rx="2" />
      <circle cx="12" cy="8" r="2" />
      <path d="M12 10v2.2M8.2 12.2h7.6M8.2 12.2v2M15.8 12.2v2" />
      <circle cx="8.2" cy="16" r="1.8" />
      <circle cx="15.8" cy="16" r="1.8" />
      <path d="M6.5 6.5h2M15.5 6.5h2M6.5 18h2M15.5 18h2" opacity={0.45} />
    </Icon>
  );
}

// ─── Landing Page Feature Icons (Larger, Duotone-ready) ──────────────────────
// These use a layered approach: a background shape + foreground icon
// Color is controlled via CSS classes or props

/** Premium landing icon wrapper for feature cards */
export function FeatureIconShell({
  children,
  bg = '#eaf3de',
  accent = '#3b6d11',
  size = 56,
}: {
  children: React.ReactNode;
  bg?: string;
  accent?: string;
  size?: number;
}) {
  return (
    <div
      style={{
        width: size,
        height: size,
        borderRadius: size * 0.35,
        background: bg,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Duotone soft shape in background */}
      <div
        style={{
          position: 'absolute',
          width: size * 1.2,
          height: size * 1.2,
          borderRadius: '50%',
          background: `radial-gradient(circle at 70% 30%, ${accent}22, transparent 65%)`,
          top: '-10%',
          left: '-10%',
        }}
      />
      <div style={{ position: 'relative', color: accent }}>{children}</div>
    </div>
  );
}

/** Export a single icon map for dynamic lookup */
export const ERTZYX_ICON_MAP = {
  living_ledger: LivingLedgerIcon,
  seven_pillars: SevenPillarsIcon,
  time_capsule: TimeCapsuleIcon,
  pulse: PulseIcon,
  journey_map: JourneyMapIcon,
  proof_cards: ProofCardsIcon,
  family_archive: FamilyArchiveIcon,
  vault: VaultDoorIcon,
  geospatial: GeospatialIcon,
  verification: VerificationIcon,
  circles: CirclesIcon,
  add_memory: AddMemoryIcon,
  journey_group: JourneyGroupIcon,
  shared_links: SharedLinksIcon,
  digital_importer: DigitalImporterIcon,
  media_vault: MediaVaultIcon,
  achievements: AchievementsIcon,
  notifications: NotificationsIcon,
  settings: SettingsIcon,
  home: HomeIcon,
  profile: ProfileIcon,
  billing: BillingIcon,
};
